import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { User, UserCredential } from 'firebase/auth';
import * as Sentry from '@sentry/react';
import analytics from '../lib/analytics';
import { applyUtmUserProperties } from '../lib/analytics-utm';
import { identifyUser as identifyTikTok } from '../lib/pixel';
import {
  sendEmailSignInLink,
  isEmailSignInLink,
  completeEmailSignIn,
  getReadyCurrentUser,
} from '../lib/auth-actions';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<UserCredential>;
  signOut: () => Promise<void>;
  getIdToken: (forceRefresh?: boolean) => Promise<string | null>;
  sendEmailSignInLink: (email: string, continueUrl: string) => Promise<void>;
  isEmailSignInLink: (href: string) => Promise<boolean>;
  completeEmailSignIn: (email: string, href: string) => Promise<UserCredential>;
  getReadyCurrentUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Hydration fix: isClient começa false em SSR e no client (mesmo valor).
  // useEffect seta true após hidratação — evita mismatch SSR/client.
  // loading=false em SSR → landing pré-renderiza sem spinner.
  const [isClient, setIsClient] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const lastIdentifiedUidRef = useRef<string | null>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    let idleHandle: number | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    const subscribe = () => {
      if (cancelled) return;
      // Importar firebase/auth dinamicamente para não quebrar o bundle SSR.
      // .catch garante que loading=false mesmo se chunk-load falhar pós-deploy.
      Promise.all([import('firebase/auth'), import('../lib/firebase')])
        .then(([{ onAuthStateChanged }, { auth }]) => {
          if (cancelled || !auth) {
            setLoading(false);
            return;
          }
          setLoading(true);
          unsubscribe = onAuthStateChanged(auth, (authUser) => {
            setUser(authUser);
            setLoading(false);
            const nextUid = authUser?.uid ?? null;
            if (nextUid === lastIdentifiedUidRef.current) return;
            lastIdentifiedUidRef.current = nextUid;

            // Sentry — associar erros ao uid (sem email/PII).
            if (nextUid) {
              Sentry.setUser({ id: nextUid });
              // PostHog — identificar usuário com Firebase UID. Sem PII (D7).
              analytics.identify(nextUid);
              // TikTok Pixel — external_id = SHA-256(uid). Sem email/telefone.
              void identifyTikTok(nextUid);
              // Aplicar UTMs first-touch como user properties (P4 — first-touch).
              applyUtmUserProperties(analytics.setUserPropertyOnce.bind(analytics));
            } else {
              Sentry.setUser(null);
              // PostHog — resetar identidade após logout (anônimo na próxima sessão).
              analytics.reset();
            }
          });
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('[AuthProvider] failed to load firebase modules', err);
          setLoading(false);
        });
    };

    // Landing pública: Firebase Auth (iframe + identitytoolkit) custa ~1s na cadeia
    // crítica e infla o LCP. Diferimos a subscribe pro idle pra liberar o paint do
    // hero. Usuários autenticados na landing veem o "/" por ~1s antes do redirect —
    // aceitável dado o ganho de LCP. Rotas autenticadas (/dashboard etc) mantém
    // subscribe imediato para não atrasar AuthGuard.
    const isLanding = window.location.pathname === '/';
    if (isLanding) {
      if (typeof window.requestIdleCallback === 'function') {
        idleHandle = window.requestIdleCallback(subscribe, { timeout: 1500 });
      } else {
        timeoutHandle = setTimeout(subscribe, 500);
      }
    } else {
      subscribe();
    }

    return () => {
      cancelled = true;
      if (idleHandle !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) clearTimeout(timeoutHandle);
      unsubscribe?.();
    };
  }, [isClient]);

  const signInWithGoogle = async (): Promise<UserCredential> => {
    const [{ signInWithPopup }, { auth, googleProvider }] = await Promise.all([
      import('firebase/auth'),
      import('../lib/firebase'),
    ]);
    if (!auth || !googleProvider) throw new Error('Firebase Auth not initialized');
    return await signInWithPopup(auth, googleProvider);
  };

  const handleSignOut = async () => {
    try {
      const [{ signOut: firebaseSignOut }, { auth }] = await Promise.all([
        import('firebase/auth'),
        import('../lib/firebase'),
      ]);
      if (!auth) throw new Error('Firebase Auth not initialized');
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  // forceRefresh força a renovação do ID token (#2): após o usuário verificar o
  // e-mail, precisamos de um token com `email_verified=true` fresco para o backend
  // conceder o bônus deferido (D3) — sem isso, só renovaria naturalmente (~1h).
  const getIdTokenForBackend = async (forceRefresh = false): Promise<string | null> => {
    if (!user) return null;
    try {
      const { getIdToken } = await import('firebase/auth');
      return await getIdToken(user, forceRefresh);
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signOut: handleSignOut,
        getIdToken: getIdTokenForBackend,
        sendEmailSignInLink,
        isEmailSignInLink,
        completeEmailSignIn,
        getReadyCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
